<template>
  <form
    class="flex flex-col items-baseline gap-8"
    @submit.prevent="submitPortingRequest"
  >
    <InputText
      v-if="showTransferOwnerSsn"
      class="full-width"
      :name="ownerSSNField"
      :input-id="ownerSSNField"
      type="text"
      label="Ägarens personnummer"
      help-text="Rätt format: ÅÅÅÅMMDDXXXX"
      placeholder="ÅÅÅÅMMDDXXXX"
      mode="eager"
      :validate="[
        'ssnNotEmpty',
        'ssnCorrectFormat',
        'ssnForFamilyPlanVerified',
      ]"
      :autocomplete="false"
      :start-value="numberConfig.ownerSocialSecurityNumber"
      :is-disabled="numberConfig.keepNumberCodeSent"
      @input="numberConfig.keepNumberCodeSent = false"
      @cleared="() => (numberConfig.keepNumberCodeSent = false)"
    />

    <div class="flex items-flex-start gap-8 w-full">
      <InputText
        v-model="numberConfig.keepNumberInputValue"
        class="full-width"
        :name="numberField"
        :input-id="numberField"
        type="text"
        label="Numret du vill behålla"
        help-text="Rätt format: 0701234567"
        placeholder="Numret du vill behålla"
        mode="passive"
        :validate="[
          'phoneNumberNotEmpty',
          'phoneNumberCorrectFormat',
          'phoneNumberForFamilyPlanNotAlreadySet',
          'phoneNumberForFamilyPlanNotSameAsParent',
          'phoneNumberToKeepHasBeenValidatedBeforeStep2Submit',
        ]"
        @input="numberConfig.keepNumberCodeSent = false"
        @cleared="() => (numberConfig.keepNumberCodeSent = false)"
      />

      <div class="flex-shrink-0 mt-11">
        <AppButton
          :show-loading="isSubmitting"
          color="primary"
          aria-label="Godkänn nummerflytt"
          squared
        >
          <nuxt-icon name="arrow-right" filled aria-hidden="true" />
        </AppButton>
      </div>
    </div>
  </form>
  <slot v-if="!numberConfig.keepNumberCodeSent" name="visibleBeforeSubmit" />
</template>

<script lang="ts" setup>
import { AddValidatorKey } from '~/formAggregator';
import type { SendPortingCodeErrorData } from '~/repository/modules/porting.module';
import type { ValidateNumberTransferResponse } from '~/models/customer/ValidateNumberTransfer.api.model';
import { KEEP_NUMBER_OPTION__NEW } from '~/models/checkout/checkout_store/NumberConfigCheckoutState.model';
import { isTestEnvironment } from '~/utils/general/isTestEnvironment';

const { $api } = useNuxtApp();
const props = defineProps<{
  cartItemId: number;
  bundleId: number;
  showTransferOwnerSsn: boolean;
  parentMsisdn?: string;
}>();

const { numberConfig } = useSelectNumberConfig({
  cartItemBundleId: props.bundleId,
  cartItemId: props.cartItemId,
});

const numberField = computed(() => {
  return `number_${props.bundleId}_${props.cartItemId}`;
});
const ownerSSNField = computed(() => {
  return `ownerSSN_${props.bundleId}_${props.cartItemId}`;
});

const checkoutStore = useMyCheckoutStore();

const { handleSubmit, validate, isSubmitting } = useForm();

// add this component's validator to the aggregator
const addValidator = inject(AddValidatorKey, () => {
  throw new Error('No aggregator provided for inject');
});
addValidator(validate);

const isFamilyItem = computed(() => {
  const item = checkoutStore.fetchedData.cart.cartItems?.find(
    (item) =>
      item.BundleId === props.bundleId &&
      (item.Id === props.cartItemId ||
        item.BundlePlanItem?.Id === props.cartItemId),
  );
  return !!(item?.BundlePlanItem?.IsFamilyPlan || item?.IsFamilyPlan);
});

const validateNumberTransfer = async (
  numberValue: string,
): Promise<ValidateNumberTransferResponse | undefined> => {
  let mainPlanNumber: string | undefined;
  let familyPlanNumber: string | undefined;

  if (isFamilyItem.value) {
    familyPlanNumber = numberValue;
    const mainCartItem = checkoutStore.fetchedData.cart.cartItems?.find(
      (item) =>
        item.BundleId != null &&
        !(item.BundlePlanItem?.IsFamilyPlan || item.IsFamilyPlan),
    );
    if (mainCartItem) {
      const id = mainCartItem.BundlePlanItem?.Id ?? mainCartItem.Id;
      const key = checkoutStore_createNumberSelectionMapKey(
        mainCartItem.BundleId!,
        id,
      );
      const mainConfig = checkoutStore.userState.numberConfigMap[key];
      mainPlanNumber =
        mainConfig?.keepNumberOption === KEEP_NUMBER_OPTION__NEW
          ? undefined
          : mainConfig?.keepNumberInputValue || undefined;
    } else {
      mainPlanNumber = props.parentMsisdn;
    }
  } else {
    mainPlanNumber = numberValue;
  }

  return await $api.CustomerModule.ValidateNumberTransfer({
    MainMsisdn: mainPlanNumber,
    ChildMsisdn: familyPlanNumber,
    CartItemId: props.cartItemId,
  });
};

const submitPortingRequest = handleSubmit(async (values, actions) => {
  const ownerSSNValue = values[ownerSSNField.value];
  const numberValue = values[numberField.value];

  if (!numberValue) {
    return;
  }

  const numberValidationResult = await validateNumberTransfer(numberValue);
  if (!numberValidationResult?.AllowTransfer) {
    actions.setFieldError(
      numberField.value,
      numberValidationResult?.DisallowTransferReason ??
        'Går inte verifiera nummer',
    );
    return;
  }

  try {
    await $api.PortingModule.sendPortingCode(
      props.cartItemId,
      numberValue,
      props.parentMsisdn,
      ownerSSNValue,
    );

    if (ownerSSNValue) {
      numberConfig.value.ownerSocialSecurityNumber = ownerSSNValue;
    }

    numberConfig.value.keepNumberCodeSent = true;

    if (isTestEnvironment()) {
      const response = await $api.PortingModule.devGetPortingCode(numberValue);
      numberConfig.value.keepNumberDevPortingCode = response as string;
    }
  } catch (error) {
    if (
      isNuxtError<SendPortingCodeErrorData>(error) &&
      error.data?.messages &&
      error.data?.messages.length > 0
    ) {
      const targetField = error.data?.owner_address_not_found
        ? ownerSSNField.value
        : numberField.value;
      actions.setFieldError(targetField, error.data.messages[0]);
    }
  }
});

onMounted(() => {
  numberConfig.value.keepNumberInputFieldId = numberField.value;
});
</script>

<style scoped lang="scss"></style>
